import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import api, { setAuthToken, markBootstrapped } from "../services/api/axios";

const STORAGE_KEY = "access_token";

const LOGIN_ENDPOINT = "/auth/login";
const ME_ENDPOINT = "/auth/me";
const LOGOUT_ENDPOINT = "/auth/logout";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

/* ---------------- helpers ---------------- */
function decodeSafe(token) {
  try {
    return token ? jwtDecode(token) || {} : {};
  } catch {
    return {};
  }
}

function looksLikeJwt(s) {
  return typeof s === "string" && s.split(".").length === 3;
}

function dbg(...args) {
  if (typeof window !== "undefined" && window.__DEBUG_AUTH) {
    window.dispatchEvent(
      new CustomEvent("auth:debug", {
        detail: args,
      }),
    );
  }
}

function buildUserData(data, decoded = {}, base = {}) {
  return {
    ...(base || {}),
    ...decoded,
    ...data,
    is_admin: data?.is_admin ?? decoded?.is_admin ?? false,
    roles: data?.roles || decoded?.roles || base?.roles || [],
    organization_guid: data?.organization_guid ?? null,
    organization_name: data?.organization_name ?? null,
  };
}

/* ==========================================================
   Auth Provider
========================================================== */
export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch {
      return null;
    }
  });
  const [authReady, setAuthReady] = useState(false);

  const refreshUser = useCallback(async () => {
    const { data } = await api.get(ME_ENDPOINT);
    const nextUser = buildUserData(data, decodeSafe(token), user);
    setUser(nextUser);
    return nextUser;
  }, [token, user]);

  /* ---------------------------------------------------------
     Bootstrap on load (restore token + load /me)
     Runs ONCE by design
  --------------------------------------------------------- */
  useEffect(() => {
    const bootstrap = async () => {
      try {
        const existing =
          typeof localStorage !== "undefined"
            ? localStorage.getItem(STORAGE_KEY)
            : null;

        dbg("bootstrap start; token exists?", !!existing);

        if (!existing) {
          setUser(null);
          setToken(null);
          return;
        }

        setAuthToken(existing);

        const decoded = decodeSafe(existing);

        // 🔹 Provisional user (JWT = identity ONLY)
        setUser((prev) => ({
          ...prev,
          ...decoded,
          is_admin: !!decoded?.is_admin,

          // 🚫 NEVER trust org data from JWT
          organization_name: null,
          organization_guid: prev?.organization_guid ?? null,
        }));

        // 🔹 Load authoritative user/org data
        try {
          const { data } = await api.get(ME_ENDPOINT);

          setUser(buildUserData(data, decoded));

          dbg("ME merged OK", { email: data?.email });
        } catch (e) {
          dbg("ME failed; keeping provisional user", e);
        }

        setToken(existing);
      } finally {
        markBootstrapped?.();
        setAuthReady(true);
        dbg("bootstrap done");
      }
    };

    bootstrap();
  }, []);

  /* ==========================================================
     LOGIN
  ========================================================== */
  const login = useCallback(async (emailOrToken, maybePassword, options = {}) => {
    /* ---------------- Raw JWT login ---------------- */
    if (looksLikeJwt(emailOrToken) && !maybePassword) {
      const raw = emailOrToken;
      dbg("login via raw JWT");

      setAuthToken(raw);
      try {
        localStorage.setItem(STORAGE_KEY, raw);
      } catch {}

      const decoded = decodeSafe(raw);

      // Provisional identity
      setUser({
        ...decoded,
        is_admin: !!decoded?.is_admin,
        organization_guid: null,
        organization_name: null, // 🚫 never from token
      });

      setToken(raw);

      try {
        const { data } = await api.get(ME_ENDPOINT);
        const nextUser = buildUserData(data, decoded);
        setUser(nextUser);
        return nextUser;
      } catch (e) {
        dbg("raw-JWT ME failed", e);
        return decoded;
      }
    }

    /* ---------------- Credential login ---------------- */
    const email = emailOrToken;
    const password = maybePassword;

    dbg("login with email:", email);

    const { data } = await api.post(LOGIN_ENDPOINT, {
      email,
      password,
      admin_login: Boolean(options.adminLogin),
    });
    const raw = data?.access_token;
    if (!raw) throw new Error("Login failed: No access_token returned");

    setAuthToken(raw);
    try {
      localStorage.setItem(STORAGE_KEY, raw);
    } catch {}

    const decoded = decodeSafe(raw);

    const returnedUser = data?.user || {};

    // Provisional identity
    setUser({
      ...decoded,
      ...returnedUser,
      is_admin: !!decoded?.is_admin,
      roles: returnedUser?.roles || decoded?.roles || [],
      organization_guid: returnedUser?.organization_guid ?? null,
      organization_name: returnedUser?.organization_name ?? null,
    });

    setToken(raw);

    try {
      const res = await api.get(ME_ENDPOINT);
      const nextUser = buildUserData(res.data, decoded, returnedUser);
      setUser(nextUser);
      return nextUser;
    } catch (e) {
      dbg("credentials ME failed", e);
      return decoded;
    }
  }, []);

  /* ==========================================================
     LOGOUT
  ========================================================== */
  const logout = useCallback(async () => {
    dbg("logout");

    try {
      await api.post(LOGOUT_ENDPOINT);
    } catch {}

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {}

    setAuthToken(null);
    setUser(null);
    setToken(null);
  }, []);

  /* ==========================================================
     CONTEXT VALUE
  ========================================================== */
  const ctx = useMemo(
    () => ({
      user,
      token,
      authReady,
      login,
      logout,
      refreshUser,
      setUser,
    }),
    [user, token, authReady, login, logout, refreshUser],
  );

  return <AuthCtx.Provider value={ctx}>{children}</AuthCtx.Provider>;
}
